import { Node, Edge } from 'reactflow';
import { ValidationResult } from '../types/flow';

// Connection validation rules
const validConnections = {
  agent: ['task'],
  task: ['tool', 'flow'],
  tool: ['task', 'flow'],
  flow: ['task']
} as const;

// Validate node requirements
export const validateNode = (node: Node): ValidationResult => {
  switch (node.type) {
    case 'agent':
      if (!node.data?.role) {
        return { valid: false, error: 'Agent requires a role' };
      }
      if (!node.data?.goals) {
        return { valid: false, error: 'Agent requires goals' };
      }
      break;
    case 'task':
      if (!node.data?.description) {
        return { valid: false, error: 'Task requires a description' };
      }
      break;
    case 'tool':
      if (!node.data?.toolType) {
        return { valid: false, error: 'Tool requires a type' };
      }
      break;
    case 'flow':
      if (node.data?.flowType === 'conditional' && !node.data?.condition) {
        return { valid: false, error: 'Conditional flow requires a condition' };
      }
      break;
  }
  return { valid: true };
};

// Validate connection rules
export const validateConnection = (
  source: Node,
  target: Node,
  edges: Edge[]
): ValidationResult => {
  // Check valid connection types
  const allowedTargets = validConnections[source.type as keyof typeof validConnections];
  if (!allowedTargets?.includes(target.type)) {
    return {
      valid: false,
      error: `${source.type} nodes cannot connect to ${target.type} nodes`
    };
  }

  // Check for existing connections
  if (target.type === 'task' && source.type === 'agent') {
    const hasAgentInput = edges.some(edge => 
      edge.target === target.id && 
      edge.source !== source.id
    );
    if (hasAgentInput) {
      return { valid: false, error: 'Task can only have one agent input' };
    }
  }

  return { valid: true };
};

// Check for cycles in the graph
export const detectCycle = (
  startNode: string,
  edges: Edge[],
  visited = new Set<string>(),
  path = new Set<string>()
): boolean => {
  if (path.has(startNode)) return true;
  if (visited.has(startNode)) return false;

  visited.add(startNode);
  path.add(startNode);

  const outgoingEdges = edges.filter(edge => edge.source === startNode);
  for (const edge of outgoingEdges) {
    if (detectCycle(edge.target, edges, visited, path)) {
      return true;
    }
  }

  path.delete(startNode);
  return false;
};

// Comprehensive flow validation
export const validateFlow = (nodes: Node[], edges: Edge[]): ValidationResult[] => {
  const results: ValidationResult[] = [];

  // Validate nodes
  nodes.forEach(node => {
    const nodeValidation = validateNode(node);
    if (!nodeValidation.valid) {
      results.push({
        ...nodeValidation,
        nodeId: node.id
      });
    }
  });

  // Validate connections
  edges.forEach(edge => {
    const source = nodes.find(n => n.id === edge.source);
    const target = nodes.find(n => n.id === edge.target);
    if (source && target) {
      const connectionValidation = validateConnection(source, target, edges);
      if (!connectionValidation.valid) {
        results.push({
          ...connectionValidation,
          edgeId: edge.id
        });
      }
    }
  });

  // Check for cycles
  nodes.forEach(node => {
    if (detectCycle(node.id, edges)) {
      results.push({
        valid: false,
        error: 'Workflow contains cycles',
        nodeId: node.id
      });
    }
  });

  return results;
};